import React, { useMemo, useState, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import useAlerts from 'utils/hooks/useAlerts';
import { Button, Toolbar, ToolbarContent, ToolbarGroup, ToolbarItem, Flex, FlexItem } from '@patternfly/react-core';
import createMutationRequest from 'utils/createMutationRequest';
// import createQueryRequest from 'services/createQueryRequest';
import useAPI from 'services/useAPI';
import { useTranslation } from 'localization/useTranslation';

import ContentPage from 'components/ContentView/ContentPage/ContentPage';
import ModelType from 'components/Model/ModelType';
import ModelsContainer from 'components/Model/ModelsContainer';
import { hasValidInput } from 'components/Model/ModelUtils';
import BreadcrumbSkeleton from 'components/Breadcrumb/BreadcrumbSkeleton';
import _ from 'lodash';
import { useUserQuery, userKeys, useUserSettingsQuery } from './queries';
import ConfirmModalWithOrg from 'components/Modals/ConfirmModalWithOrg';
import useBoolean from 'utils/hooks/useBoolean';
import { privileges } from 'utils/types';

export const UserName = ({ userId }) => {
  const userQuery = useUserQuery(userId);
  return userQuery.isLoading ? <BreadcrumbSkeleton /> : (userQuery.data?.displayName ?? '');
};


const generatePassword = () => {
  const length = 12;
  const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@%()_-=+';
  let retVal = '';
  for (var i = 0, n = charset.length; i < length; ++i) {
    retVal += charset.charAt(Math.floor(Math.random() * n));
  }
  return retVal;
};


const initialSettings = {
  licenseAccepted: false,
  claims: {
    isDeveloper: false,
    licenseAccepted: false
  },
  language: 'en',
  units: 'metric'
};

const AdminUserPage = ({ breadcrumbs }) => {
  const T = useTranslation();
  const api = useAPI();
  const { userId } = useParams();
  const queryClient = useQueryClient();
  const history = useHistory();
  const [password] = useState(generatePassword());
  const [editedUser, setUser] = useState(userId ? {} : { password });
  const [editedSettings, setSettings] = useState({});
  const [alerts, addSuccessAlert, addErrorAlert] = useAlerts();
  const [isOpen, showPublishDialog, hidePublishDialog] = useBoolean();

  const validateEmail = (email) => {
    if (!email) {
      return true;
    }
    const re = /\S+@\S+\.\S+/;
    return !re.test(email);
  };

  const userModel = useMemo(() => {
    const base = [
      {
        type: ModelType.TEXT,
        path: 'displayName',
        label: 'Display Name',
        isRequired: true,
        hasError: _.isEmpty
      },
      {
        type: ModelType.TEXT,
        path: 'email',
        label: 'Email',
        isRequired: true,
        hasError: validateEmail
      },
      {
        type: ModelType.TEXT,
        path: 'phoneNumber',
        label: 'Phone'
      }
    ];
    const passwordModel = [
      {
        type: ModelType.TEXT,
        path: 'password',
        label: 'Password',
        isRequired: true,
        hasError: _.isEmpty
      }
      // {
      //   type: ModelType.CHECKBOX,
      //   path: 'changeOnFirstLogin',
      //   label: 'Change Password on First Login'
      // }
    ];
    return [...base, ...(userId ? [] : passwordModel)];
  }, [userId]);

  const settingsModel = useMemo(() => {
    return [
      {
        type: ModelType.SWITCH,
        label: T.admin.claims.isDeveloper,
        path: 'claims.isDeveloper',
        disableTitle: true,
        properties: {
          isDisabled: !userId
        }
      },
      {
        type: ModelType.SWITCH,
        label: 'Has Accepted License',
        path: 'licenseAccepted',
        disableTitle: true
      },
      {
        type: ModelType.OPTIONSV2,
        label: T.common.language,
        path: 'language',
        isRequired: true,
        options: _.map({ en: 'English', sv: 'Svenska' }, (text, value) => ({ text, value }))
      },
      {
        type: ModelType.OPTIONSV2,
        label: 'Units',
        path: 'units',
        isRequired: true,
        options: _.map({ metric: 'Metric', imperial: 'Imperial' }, (text, value) => ({ text, value }))
      }
    ];
  }, [T.admin.claims.isDeveloper, T.common.language, userId]);


  const userQuery = useUserQuery(userId);
  const settings = useUserSettingsQuery(userId);
  const settingsQuery = useMutation({
    mutationFn: createMutationRequest(api.core.updateUserSettings),
    onMutate: async (settings) => {
      await queryClient.cancelQueries(userKeys.userSettings(settings.id));
      const oldSettings = queryClient.getQueryData(userKeys.userSettings(settings.id));
      queryClient.setQueryData(userKeys.user(settings.id), settings);
      return { oldSettings };
    },
    onSuccess: () => {
      settings.refetch();
    },
    onError: (error, settings, context) => {
      addErrorAlert(error);
      queryClient.setQueryData(userKeys.userSettings(settings.id), context.oldSettings);
    }
  });

  const createUserMutation = useMutation({
    mutationFn: createMutationRequest(api.core.createUser),
    onSuccess: (user) => {
      addSuccessAlert(T.admin.createuser.success);
      queryClient.invalidateQueries(['users']);
      const settingsData = { ...editedSettings, userId: user.id };
      settingsQuery.mutate(settingsData);
      history.replace(`/admin/users/${user.id}`);
    }
  });

  const updateUserMutation = useMutation({
    mutationFn: createMutationRequest(api.core.updateUser),
    onSuccess: () => {
      userQuery.refetch();
      queryClient.invalidateQueries(['user', userId]);
      const settingsData = _.isEmpty(editedSettings) ? { ...settings.data, userId: userId } : { ...editedSettings, userId: userId };
      settingsQuery.mutate({ ...settingsData });
    },
    onError: (error) => {
      addErrorAlert(error);
    }
  });

  const deleteUserMutation = useMutation({
    mutationFn: createMutationRequest(api.core.deleteUser),
    onSuccess: () => {
      queryClient.invalidateQueries(['user', userId]);
      history.push('/admin/users');
    },
    onError: (error) => {
      addErrorAlert(error);
    }
  });

  const updateUser = useCallback((newData) => {
    if (_.isEmpty(newData)) {
      const settingsData = _.isEmpty(editedSettings) ? { ...settings.data, userId: userId } : { ...editedSettings, userId: userId };
      settingsQuery.mutate({ ...settingsData });
    } else {
      const updated = _.pick(newData, ['displayName', 'phoneNumber']);
      updateUserMutation.mutate({ ...updated, userId });
    }
  }, [updateUserMutation, editedSettings, settings.data, settingsQuery, userId]);
  const canSave = useMemo(() => {
    const isValid = hasValidInput(_.merge({}, userQuery.data, editedUser), userModel);
    const isChanged = (!_.isEmpty(editedUser) && !_.isEqual(editedUser, userQuery.data)) || (!_.isEmpty(editedSettings) && !_.isEqual(editedSettings, { ...settings.data, userId: editedSettings.userId }));

    return isValid && isChanged;
  }, [editedUser, userModel, userQuery.data, editedSettings, settings.data]);

  const checkOrg = useCallback(() => {
    if (userId) {
      updateUser(editedUser);
    } else {
      showPublishDialog();
    }
  }, [editedUser, showPublishDialog, updateUser, userId]);

  const onPublishToOrg = useCallback((user, currentOrg) => {
    hidePublishDialog();
    createUserMutation.mutate({
      ...user,
      organizationId: currentOrg.id
    });
  }, [createUserMutation, hidePublishDialog]);

  const contentToolbar = useMemo(() => {
    return (
      <Toolbar>
        <ToolbarContent>
          <ToolbarGroup>
            <ToolbarItem>
              <Button
                variant="primary"
                isDisabled={!canSave}
                isLoading={createUserMutation.isLoading || updateUserMutation.isLoading}
                onClick={checkOrg}
              >
                { userId ? T.common.update : T.common.create }
              </Button>
            </ToolbarItem>
            <ToolbarItem>
              <Button
                variant="danger"
                onClick={() => {
                  deleteUserMutation.mutate(userId);
                }}
              >
                { T.common.delete }
              </Button>
            </ToolbarItem>
          </ToolbarGroup>
        </ToolbarContent>
      </Toolbar>
    );
  }, [T.common.create, T.common.delete, T.common.update, canSave, checkOrg, createUserMutation.isLoading, deleteUserMutation, updateUserMutation.isLoading, userId]);

  return (
    <ContentPage breadcrumb={breadcrumbs} toolbar={contentToolbar} help="admin/users/edit">
      { alerts }
      <Flex>
        <FlexItem>
          <ModelsContainer
            models={userModel}
            data={_.merge({}, userQuery.data, editedUser)}
            setData={setUser}
            // isLoading={userQuery.isInitialLoading}
          />
        </FlexItem>
        <FlexItem >
          <ModelsContainer
            models={settingsModel}
            data={_.merge(initialSettings, settings.data, editedSettings)}
            setData={newSettings => {
              const updated = { ...settings.data, ...newSettings, userId };
              setSettings(updated);
            }}
            isLoading={settings.isLoading || settingsQuery.isLoading}
          />
        </FlexItem>
      </Flex>
      <ConfirmModalWithOrg
        // TODO: Translate
        title={'Publish to Organization'}
        icon={'info'}
        isOpen={isOpen}
        item={editedUser}
        confirmText={T.admin.common.publish}
        onConfirm={onPublishToOrg}
        onCancel={hidePublishDialog}
        template={'Connect this user to below organization.'}
        args={[editedUser?.displayName]}
        neededPrivilege={privileges.UserManagement}
      />
    </ContentPage>
  );
};

export default AdminUserPage;
